"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { Fragment, useCallback, useEffect, useId, useRef, useState } from "react";
import { TemplateCardsList, TemplateScrollButtons } from "./TemplateCards";

/* ---------- Hero background grid (old grid — solid dots + 6 columns) ---------- */

const HERO_GRID_COLS = Array.from({
  length: 6
});
const HERO_GRID_DOTS = Array.from({
  length: 7
});
function HeroGridColumns() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-1 px-[9px]">{HERO_GRID_COLS.map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, col) {
  return <div key={col} className={`border-ds-neutral-200 flex-1 border-l ${col === 5 ? "border-r" : ""}`} />;
}
function HeroGridDotRow() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex h-[19px] shrink-0 items-center gap-2">{HERO_GRID_DOTS.map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/* ---------- Scrolling CTA placeholders ---------- */
function _temp2(_, i) {
  return <Fragment key={i}><div className="flex size-[19px] shrink-0 items-center justify-center"><div className="bg-ds-neutral-300 size-[6px] rounded-full" /></div>{i < 6 && <div className="bg-ds-neutral-200 h-px flex-1" />}</Fragment>;
}
const HERO_PLACEHOLDERS = ["Build an onchain workflow with Caddie AI", "Automate copytrading polymarket user bobe2", "Build an automation to rebalance my Morpho yield into highest APY vaults", "Pay contractors in gsheets every monday at 9am UTC", "Send a slack approval when an invoice is received in my gmail, pay in USDC on Arb", "Sweep the dust in my 5 wallets daily and swap for USDC on arb"];

/* ---------- Node icon assets ---------- */

const IMG_TOKEN_PRICE = "/images/homepage/hero-token-price.svg";
const IMG_IF_THEN_ELSE = "/images/homepage/hero-if-then-else.svg";
const IMG_GSHEETS_HERO = "/images/homepage/hero-gsheets.png";
const IMG_SLACK_HERO = "/images/homepage/hero-slack.png";

/* ---------- Floating workflow nodes (decorative) ---------- */

interface FloatingNode {
  id: string;
  label: string;
  typeLabel: string;
  typeColor: string;
  iconBg: string;
  iconSrc: string;
  position: string;
  side: "left" | "right";
  /** Optional second row inside the card (e.g. CTA button) */
  extra?: "trigger-button";
  /** Zoom/crop icon to fill container (removes internal whitespace) */
  iconCrop?: boolean;
  /** Override default icon size (e.g. "size-[24px]") */
  iconSize?: string;
  /** Override default card width (default 250px) */
  cardWidth?: string;
}
const floatingNodes: FloatingNode[] = [{
  id: "token-price",
  label: "Token Price Trigger (CEXes)",
  typeLabel: "Trigger",
  typeColor: "#2450ff",
  iconBg: "rgba(36,80,255,0.15)",
  iconSrc: IMG_TOKEN_PRICE,
  position: "left-[5%] top-[20%]",
  side: "left",
  extra: "trigger-button",
  cardWidth: "w-[262px]"
}, {
  id: "if-then-else",
  label: "If-Then-Else",
  typeLabel: "Logic",
  typeColor: "#7f56d9",
  iconBg: "rgba(127,86,217,0.15)",
  iconSrc: IMG_IF_THEN_ELSE,
  iconSize: "size-[24px]",
  position: "left-[3%] top-[42%]",
  side: "left"
}, {
  id: "google-sheets",
  label: "Google Sheets: Get Values",
  typeLabel: "Action",
  typeColor: "#14a265",
  iconBg: "transparent",
  iconSrc: IMG_GSHEETS_HERO,
  position: "right-[4%] top-[41%]",
  side: "right"
}, {
  id: "send-slack",
  label: "Send Slack Message",
  typeLabel: "Action",
  typeColor: "#14a265",
  iconBg: "transparent",
  iconSrc: IMG_SLACK_HERO,
  position: "right-[6%] top-[18%]",
  side: "right",
  iconCrop: true,
  cardWidth: "w-[276px]"
}];

/* ---------- NodeCard with forwarded ref ---------- */

interface NodeCardProps {
  node: FloatingNode;
  nodeRef: (el: HTMLDivElement | null) => void;
}
function NodeCard(t0) {
  const $ = _c(33);
  const {
    node,
    nodeRef
  } = t0;
  const t1 = `pointer-events-auto absolute ${node.position} ${node.cardWidth ?? "w-[250px]"} rounded-ds-18 border-ds-alpha-black-10 bg-ds-neutral-250 shadow-ds-card-halo border p-1.5 transition-all duration-200 ease-out hover:z-20 hover:scale-105 motion-reduce:transition-none motion-reduce:hover:scale-100`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="pointer-events-none absolute inset-0 rounded-xl mix-blend-soft-light" style={{
      backgroundImage: "linear-gradient(-12deg, rgba(23,22,25,0.4) 0%, rgba(229,229,229,0.4) 105%)"
    }} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== node.iconBg) {
    t3 = {
      backgroundColor: node.iconBg
    };
    $[1] = node.iconBg;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = `object-contain ${node.iconBg === "transparent" ? "size-full" : node.iconSize ?? "size-[35px]"} ${node.iconCrop ? "scale-[1.7]" : ""}`;
  let t5;
  if ($[3] !== node.iconSrc || $[4] !== node.label || $[5] !== t4) {
    t5 = <img alt={node.label} src={node.iconSrc} className={t4} />;
    $[3] = node.iconSrc;
    $[4] = node.label;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center overflow-clip rounded-lg" style={t3}>{t5}</div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== node.label) {
    t7 = <p className="text-ds-text-tertiary truncate text-sm font-medium leading-tight">{node.label}</p>;
    $[10] = node.label;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== node.typeColor) {
    t8 = {
      color: node.typeColor
    };
    $[12] = node.typeColor;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== node.typeLabel || $[15] !== t8) {
    t9 = <p className="text-2xs leading-tight" style={t8}>{node.typeLabel}</p>;
    $[14] = node.typeLabel;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t9) {
    t10 = <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-0.5">{t7}{t9}</div>;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6) {
    t11 = <div className="border-ds-alpha-black-10 shadow-ds-card-halo flex items-center gap-2 border-b p-3">{t6}{t10}</div>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== node.extra) {
    t12 = node.extra === "trigger-button" && <div className="border-ds-alpha-black-10 shadow-ds-card-halo border-b p-2"><div className="border-ds-text-tertiary from-ds-neutral-700 to-ds-zinc-600 flex h-[29px] items-center justify-center gap-1 rounded-lg border-[1.5px] bg-gradient-to-t"><svg className="h-3.5 w-3.5" viewBox="0 0 13 13" fill="none"><g clipPath="url(#hero-cursor)"><path d="M6.49273 11.6484L6.01107 11.7826L6.32829 12.9214L6.92449 11.9006L6.49273 11.6484ZM12.1872 1.89844L12.6189 2.1506L13.0582 1.39844H12.1872V1.89844ZM1.08301 1.89844V1.39844H-0.0745475L0.718982 2.2412L1.08301 1.89844ZM4.90854 5.96131L5.3902 5.82714L5.3571 5.70833L5.27256 5.61855L4.90854 5.96131ZM11.9457 2.60586L12.3823 2.36216L11.8949 1.48898L11.4583 1.73268L11.702 2.16927L11.9457 2.60586ZM6.49273 11.6484L6.92449 11.9006L12.6189 2.1506L12.1872 1.89844L11.7554 1.64627L6.06097 11.3963L6.49273 11.6484ZM12.1872 1.89844V1.39844H1.08301V1.89844V2.39844H12.1872V1.89844ZM1.08301 1.89844L0.718982 2.2412L4.54451 6.30407L4.90854 5.96131L5.27256 5.61855L1.44703 1.55568L1.08301 1.89844ZM4.90854 5.96131L4.42687 6.09548L6.01107 11.7826L6.49273 11.6484L6.97439 11.5143L5.3902 5.82714L4.90854 5.96131ZM5.01879 5.89976L5.26249 6.33635L11.9457 2.60586L11.702 2.16927L11.4583 1.73268L4.77509 5.46317L5.01879 5.89976Z" fill="#E5E5E5" /></g><defs><clipPath id="hero-cursor"><rect width="13" height="13" fill="white" /></clipPath></defs></svg><span className="text-ds-neutral-150 text-2xs font-medium [text-shadow:0_0_1.5px_black]">Send tokens to trigger</span></div></div>;
    $[23] = node.extra;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="shadow-ds-inner-bloom pointer-events-none absolute inset-0 rounded-xl" />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t11 || $[27] !== t12) {
    t14 = <div className="bg-ds-neutral-150 shadow-ds-neumorphic relative overflow-hidden rounded-xl">{t2}{t11}{t12}{t13}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== nodeRef || $[30] !== t1 || $[31] !== t14) {
    t15 = <div ref={nodeRef} className={t1}>{t14}</div>;
    $[29] = nodeRef;
    $[30] = t1;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}

/* ---------- AI Input (decorative) with scrolling placeholders ---------- */

function AIInput(t0) {
  const $ = _c(20);
  const {
    inputRef
  } = t0;
  const [currentIdx, setCurrentIdx] = useState(0);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const id = setInterval(() => {
        setCurrentIdx(_temp3);
      }, 3000);
      return () => clearInterval(id);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="rounded-ds-md pointer-events-none absolute inset-0 bg-white" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="rounded-ds-md pointer-events-none absolute inset-0 mix-blend-soft-light backdrop-blur-[6px]" style={{
      backgroundImage: "linear-gradient(-10deg, rgba(23,22,25,0.4) 0%, rgba(229,229,229,0.4) 105%)"
    }} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      y: 12,
      opacity: 0
    };
    t6 = {
      y: 0,
      opacity: 1
    };
    t7 = {
      y: -12,
      opacity: 0
    };
    t8 = {
      duration: 0.3,
      ease: "easeInOut"
    };
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  const t9 = HERO_PLACEHOLDERS[currentIdx];
  let t10;
  if ($[8] !== currentIdx || $[9] !== t9) {
    t10 = <div className="relative h-[56px] w-full overflow-hidden px-2 py-2 md:h-[35px]"><AnimatePresence mode="wait"><motion.p key={currentIdx} initial={t5} animate={t6} exit={t7} transition={t8} className="text-ds-alpha-black-50 leading-label absolute inset-x-2 line-clamp-2 text-base md:truncate">{t9}</motion.p></AnimatePresence></div>;
    $[8] = currentIdx;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex items-center gap-[5px]"><img src="/caddie/caddie-default.svg" alt="Caddie" className="h-6 w-6" /><span className="text-ds-focus-brand pt-0.5 text-sm font-medium">ASK CADDIE</span></div>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex h-10 w-10 items-center justify-center rounded-lg"><svg className="h-5 w-5" viewBox="0 0 20 20" fill="none"><rect x="6.5" y="2.5" width="7" height="10" rx="3.5" stroke="#565E80" strokeWidth="1.5" /><path d="M4.5 12C4.5 15.04 6.96 17.5 10 17.5C13.04 17.5 15.5 15.04 15.5 12" stroke="#565E80" strokeWidth="1.5" strokeLinecap="round" /><line x1="10" y1="17.5" x2="10" y2="19" stroke="#565E80" strokeWidth="1.5" strokeLinecap="round" /></svg></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="relative flex w-full items-end justify-between">{t11}<div className="flex items-center gap-2">{t12}<div className="from-ds-neutral-100 to-ds-neutral-200 shadow-ds-white relative flex h-10 w-10 items-center justify-center overflow-clip rounded-lg bg-gradient-to-b"><svg className="h-5 w-5" viewBox="0 0 14 14" fill="none"><path d="M7 12V2M7 2L2 7M7 2L12 7" stroke="#565E80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg><div className="rounded-inherit shadow-ds-inner-light pointer-events-none absolute inset-0" /></div></div></div>;
    t14 = <div className="rounded-inherit shadow-ds-inner-bloom pointer-events-none absolute inset-0" />;
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  let t15;
  if ($[15] !== t10) {
    t15 = <Link href="/workflows/edit" className="rounded-ds-22 border-ds-neutral-200 block cursor-pointer border bg-white p-[10px] transition-transform hover:scale-[1.02]"><div className="rounded-ds-md border-ds-neutral-200 shadow-ds-elev-3 relative flex w-full flex-col gap-3 overflow-clip border p-[15px]">{t3}{t4}{t10}{t13}{t14}</div></Link>;
    $[15] = t10;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  let t16;
  if ($[17] !== inputRef || $[18] !== t15) {
    t16 = <div ref={inputRef} className="relative z-10 mx-auto w-full max-w-[600px]">{t15}</div>;
    $[17] = inputRef;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  return t16;
}

/* ---------- Dynamic connection lines ---------- */
function _temp3(prev) {
  return (prev + 1) % HERO_PLACEHOLDERS.length;
}
interface ConnectionPath {
  d: string;
  /** Extended path for comet animation — goes past endpoints so comet exits smoothly */
  motionD: string;
  side: "left" | "right";
}
function useConnectionPaths(sectionRef: React.RefObject<HTMLElement | null>, aiInputRef: React.RefObject<HTMLDivElement | null>, nodeElMap: React.MutableRefObject<Map<string, HTMLDivElement>>) {
  const [paths, setPaths] = useState<ConnectionPath[]>([]);
  const [viewBox, setViewBox] = useState("0 0 0 0");
  const calculate = useCallback(() => {
    const section = sectionRef.current;
    const aiInput = aiInputRef.current;
    if (!section || !aiInput) return;
    const sr = section.getBoundingClientRect();
    const ar = aiInput.getBoundingClientRect();
    setViewBox(`0 0 ${sr.width} ${sr.height}`);

    // AI input edge points relative to section
    const aiLeftX = ar.left - sr.left;
    const aiRightX = ar.right - sr.left;
    const aiTopY = ar.top - sr.top;
    const aiH = ar.height;

    // Group nodes by side with their measured center Y, sorted top-to-bottom
    const sides: Record<"left" | "right", {
      node: FloatingNode;
      nr: DOMRect;
    }[]> = {
      left: [],
      right: []
    };
    floatingNodes.forEach(node => {
      const el = nodeElMap.current.get(node.id);
      if (!el) return;
      sides[node.side].push({
        node,
        nr: el.getBoundingClientRect()
      });
    });
    sides.left.sort((a, b) => a.nr.top - b.nr.top);
    sides.right.sort((a_0, b_0) => a_0.nr.top - b_0.nr.top);

    // Distribute attachment points evenly along AI input edge
    const attachY = (index: number, total: number) => {
      if (total === 1) return aiTopY + aiH / 2;
      const gap = 30;
      const totalSpread = gap * (total - 1);
      return aiTopY + aiH / 2 - totalSpread / 2 + index * gap;
    };
    const newPaths: ConnectionPath[] = [];

    // Left nodes → AI input (each at a distinct attachment point)
    const EXT = 50; // px to extend past endpoints so comet exits smoothly
    sides.left.forEach(({
      nr
    }, i) => {
      const startX = nr.right - sr.left;
      const startY = nr.top - sr.top + nr.height / 2;
      const endX = aiLeftX;
      const endY = attachY(i, sides.left.length);
      const midX = (startX + endX) / 2;
      const d = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
      newPaths.push({
        d,
        motionD: `M ${endX + EXT} ${endY} L ${endX} ${endY} C ${midX} ${endY}, ${midX} ${startY}, ${startX} ${startY} L ${startX - EXT} ${startY}`,
        side: "left"
      });
    });

    // AI input → right nodes (each from a distinct attachment point)
    sides.right.forEach(({
      nr: nr_0
    }, i_0) => {
      const startX_0 = aiRightX;
      const startY_0 = attachY(i_0, sides.right.length);
      const endX_0 = nr_0.left - sr.left;
      const endY_0 = nr_0.top - sr.top + nr_0.height / 2;
      const midX_0 = (startX_0 + endX_0) / 2;
      const d_0 = `M ${startX_0} ${startY_0} C ${midX_0} ${startY_0}, ${midX_0} ${endY_0}, ${endX_0} ${endY_0}`;
      newPaths.push({
        d: d_0,
        motionD: `M ${startX_0 - EXT} ${startY_0} L ${startX_0} ${startY_0} C ${midX_0} ${startY_0}, ${midX_0} ${endY_0}, ${endX_0} ${endY_0} L ${endX_0 + EXT} ${endY_0}`,
        side: "right"
      });
    });
    setPaths(newPaths);
  }, [sectionRef, aiInputRef, nodeElMap]);
  useEffect(() => {
    // Calculate after first paint so refs are attached
    const raf = requestAnimationFrame(calculate);
    const observer = new ResizeObserver(calculate);
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [calculate, sectionRef]);
  return {
    paths,
    viewBox
  };
}

/* ---------- Hero Section ---------- */

export function HeroSection() {
  const uid = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const aiInputRef = useRef<HTMLDivElement>(null);
  const nodeElMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const templateScrollRef = useRef<HTMLDivElement>(null);
  const setNodeRef = useCallback((id: string) => (el: HTMLDivElement | null) => {
    if (el) {
      nodeElMap.current.set(id, el);
    } else {
      nodeElMap.current.delete(id);
    }
  }, []);
  const {
    paths,
    viewBox
  } = useConnectionPaths(sectionRef, aiInputRef, nodeElMap);
  return <section ref={sectionRef} className="relative flex flex-col items-center justify-center overflow-hidden px-4 pb-0 md:min-h-dvh md:px-6 md:pb-0 lg:px-[160px]">
      {/* Hidden floating nodes — preserved but unused */}
      <div className="pointer-events-none absolute inset-0 z-10 hidden">
        {paths.length > 0 && <svg className="absolute inset-0 h-full w-full" viewBox={viewBox} fill="none">
            <defs>
              <linearGradient id={`${uid}-comet-mask-grad`}>
                <stop offset="0" stopColor="black" />
                <stop offset="0.85" stopColor="white" />
                <stop offset="1" stopColor="black" />
              </linearGradient>
              <filter id={`${uid}-hero-pulse-glow`} x="-200%" y="-200%" width="500%" height="500%" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset />
                <feGaussianBlur stdDeviation="3.44" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.435294 0 0 0 0 0.545098 0 0 0 0 0.984314 0 0 0 1 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape" />
              </filter>
              {/* One mask per path — gradient rect travels along each curve */}
              {paths.map((_, i) => <mask id={`${uid}-comet-mask-${i}`} key={`mask-${i}`}>
                  <rect x="-9999" y="-9999" width="99999" height="99999" fill="black" />
                  <rect x="-30" y="-10" width="35" height="20" fill={`url(#${uid}-comet-mask-grad)`}>
                    <animateMotion dur={`${3 + i * 0.7}s`} repeatCount="indefinite" rotate="auto">
                      <mpath href={`#${uid}-hero-conn-motion-${i}`} />
                    </animateMotion>
                  </rect>
                </mask>)}
            </defs>
            {paths.map(({
          d,
          motionD
        }, i_0) => {
          return <g key={i_0}>
                  {/* Base dashed line */}
                  <path d={d} stroke="rgba(23,22,25,0.12)" strokeWidth="1.5" strokeDasharray="6 4" fill="none" />
                  {/* Extended motion path (hidden) — comet animates along this */}
                  <path id={`${uid}-hero-conn-motion-${i_0}`} d={motionD} fill="none" stroke="none" />
                  {/* Blue comet stroke — uses extended path so comet travels past endpoints */}
                  <path d={motionD} stroke="#2450ff" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" mask={`url(#${uid}-comet-mask-${i_0})`} filter={`url(#${uid}-hero-pulse-glow)`} />
                </g>;
        })}
          </svg>}

        {/* Node cards — constrained to prevent spreading on ultrawide */}
        <div className="absolute inset-y-0 left-1/2 w-full max-w-[1440px] -translate-x-1/2">
          {floatingNodes.map(node => <NodeCard key={node.id} node={node} nodeRef={setNodeRef(node.id)} />)}
        </div>
      </div>

      {/* Hero content wrapper — flex-1 so old grid fills exactly to divider */}
      <div className="relative flex w-full flex-col items-center pt-5 md:pt-12 lg:flex-1">
        {/* Old grid overlay — breaks out of section lg:px-[160px] to match page-level new grid width */}
        <div className="pointer-events-none absolute inset-y-0 z-[1] hidden justify-center overflow-hidden lg:-left-[160px] lg:-right-[160px] lg:flex" aria-hidden="true">
          <div className="flex h-full w-full max-w-[1440px] flex-col">
            <HeroGridColumns />
            <HeroGridDotRow />
            <HeroGridColumns />
            <HeroGridDotRow />
            <HeroGridColumns />
          </div>
        </div>

        <div className="relative z-[2] mx-auto my-auto flex max-w-[1120px] flex-col items-center gap-10">
          {/* Headline */}
          <div className="flex flex-col items-center gap-5">
            <h1 className="leading-label text-balance text-center text-4xl font-medium tracking-[-1.44px] md:max-w-[1080px] md:text-7xl">
              <span className="text-ds-text-subtle">The Onchain Engine for Agentic AI</span>
            </h1>
            <p className="text-ds-text-subtle leading-body max-w-[560px] text-center text-lg md:text-xl">
              Build onchain apps and automations that connect wallets, smart contracts, and 2,000+ tools you already use
            </p>
          </div>

          {/* AI Input */}
          <AIInput inputRef={aiInputRef} />
        </div>

        {/* Template scroll buttons — inside old grid area */}
        <div className="relative z-[2] mx-auto hidden w-full max-w-[1440px] justify-end px-5 pb-3 pt-3 lg:flex lg:pt-0">
          <TemplateScrollButtons scrollRef={templateScrollRef} />
        </div>
      </div>

      {/* New grid divider between hero content and template cards — breaks out of section padding */}
      <div className="relative z-[3] hidden items-center justify-center gap-3 lg:mx-[-160px] lg:flex lg:h-[19px] lg:w-[calc(100%+340px)]">
        <div className="bg-ds-neutral-200 h-px min-w-0 flex-1" />
        <div className="flex w-full max-w-[1440px] items-center gap-2">
          <div className="border-ds-neutral-300 bg-ds-surface-primary size-[19px] shrink-0 rounded-full border" />
          <div className="bg-ds-neutral-200 h-px min-w-0 flex-1" />
          <div className="border-ds-neutral-300 bg-ds-surface-primary size-[19px] shrink-0 rounded-full border" />
        </div>
        <div className="bg-ds-neutral-200 h-px min-w-0 flex-1" />
      </div>

      {/* Template Cards — with left/right grid borders */}
      <div className="relative -mx-4 mt-7 w-[calc(100%+32px)] md:-mx-10 md:w-[calc(100%+80px)] lg:mx-[-160px] lg:mt-0 lg:w-[calc(100%+320px)]">
        <div className="pointer-events-none absolute inset-0 z-[1] hidden justify-center overflow-hidden lg:flex" aria-hidden="true">
          <div className="w-full max-w-[1440px] px-[9px]">
            <div className="border-ds-neutral-200 h-full border-l border-r" />
          </div>
        </div>
        <div className="lg:mx-5">
          <TemplateCardsList scrollRef={templateScrollRef} />
          <div className="flex justify-center pb-6 pt-1 lg:hidden">
            <TemplateScrollButtons scrollRef={templateScrollRef} size="compact" />
          </div>
        </div>
      </div>
    </section>;
}